import React, { useState, useEffect, useRef } from "react";
import { Mail, MapPin, CheckCircle2, ArrowRight, Target, Shield, Award, Globe } from "lucide-react";

export default function App() {
  const [page, setPage] = useState("home");
  const [activeCategory, setActiveCategory] = useState(null);
  const [lang, setLang] = useState("ar"); // "ar" for Arabic, "en" for English
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  
  // حالة التحكم في ظهور الشريط العلوي
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // ميزة إخفاء الشريط وإظهاره بسلاسة تامة
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setIsHeaderVisible(true);
      } else {
        const diff = currentScrollY - lastScrollY.current;
        if (diff > 10) {
          setIsHeaderVisible(false);
        } 
        else if (diff < -10) {
          setIsHeaderVisible(true);
        }
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // قاموس الترجمة لجميع نصوص الموقع
  const translations = {
    ar: {
      nav: { home: "الرئيسية", about: "من نحن", products: "المنتجات", partners: "الشركاء", contact: "تواصل معنا" },
      hero: {
        welcome: "مرحباً بك في",
        desc: "شريكك الموثوق في توريد الكيماويات، المستلزمات الطبية، ومستلزمات الضيافة بأعلى معايير الجودة العالمية والأسعار التنافسية.",
        btnProducts: "استعرض منتجاتنا",
        btnAbout: "تعرف علينا أكثر"
      },
      about: {
        title: "من نحن",
        headline: "قصة نجاحنا والتزامنا",
        desc: "نحن في شركة <strong>Global Golden Elements</strong> نؤمن بأن الجودة والموثوقية هما أساس النجاح الصناعي. تأسسنا لنكون الرابط الأقوى بين المصانع والشركات وأفضل الموردين العالميين في مجالات الكيماويات والمختبرات والسلامة.",
        visionTitle: "رؤيتنا",
        visionDesc: "أن نكون الخيار الأول والوجهة الموثوقة محلياً وإقليمياً في توريد المواد الصناعية الأساسية بأعلى معايير الأمان والابتكار.",
        missionTitle: "رسالتنا",
        missionDesc: "تقديم منتجات عالية الجودة، وبناء شراكات استراتيجية طويلة الأمد مع عملائنا لضمان استمرارية أعمالهم بنجاح وأمان.",
        valuesTitle: "قيمنا",
        valuesDesc: "النزاهة والشفافية في التعامل، الالتزام بمواعيد التسليم، الجودة غير المساومة، ووضع رضا العميل في قمة أولوياتنا."
      },
      products: {
        title: "منتجاتنا",
        headline: "أقسام التوريد",
        desc: "اختر القسم لاستعراض المنتجات المتاحة وطلب عروض الأسعار لاحتياجات شركتك.",
        viewProducts: "عرض المنتجات"
      },
      category: {
        back: "العودة للأقسام",
        commonProducts: "قائمة المنتجات الشائعة في هذا القسم:",
        notFound: "هل تبحث عن منتج محدد غير مدرج هنا؟",
        requestQuote: "اطلب عرض سعر الآن"
      },
      contact: {
        title: "تواصل معنا",
        headline: "جاهزون لتلبية احتياجات شركتك",
        desc: "أرسل لنا استفساراتك أو طلبات عروض الأسعار، وسيقوم فريقنا المختص بالرد عليك بأسرع وقت ممكن.",
        email: "البريد الإلكتروني",
        hq: "المقر الرئيسي",
        location: "المملكة العربية السعودية - الرياض",
        formTitle: "أرسل رسالة",
        nameLabel: "اسم الشركة / الشخص",
        namePlaceholder: "أدخل الاسم...",
        emailLabel: "البريد الإلكتروني",
        emailPlaceholder: "example@domain.com",
        msgLabel: "الرسالة / طلب عرض السعر",
        msgPlaceholder: "اكتب تفاصيل المنتجات والكميات المطلوبة هنا...",
        sendBtn: "إرسال الاستفسار",
        sending: "جاري الإرسال...",
        successMsg: "تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.",
        errorMsg: "حدث خطأ أثناء الإرسال. يرجى المحاولة لاحقاً."
      },
      partners: {
        title: "شركاؤنا",
        headline: "نعتز بشراكاتنا العالمية"
      },
      footer: {
        rights: "جميع الحقوق محفوظة."
      }
    },
    en: {
      nav: { home: "Home", about: "About Us", products: "Products", partners: "Partners", contact: "Contact Us" },
      hero: {
        welcome: "WELCOME TO",
        desc: "Your trusted partner in supplying chemicals, medical supplies, and hospitality equipment with the highest global quality standards and competitive prices.",
        btnProducts: "Explore Products",
        btnAbout: "Learn More"
      },
      about: {
        title: "ABOUT US",
        headline: "Our Success Story & Commitment",
        desc: "At <strong>Global Golden Elements</strong>, we believe quality and reliability are the foundation of industrial success. We were established to be the strongest link between factories, companies, and top global suppliers in chemicals, laboratories, and safety.",
        visionTitle: "Our Vision",
        visionDesc: "To be the first choice and trusted destination locally and regionally in supplying essential industrial materials with the highest standards of safety and innovation.",
        missionTitle: "Our Mission",
        missionDesc: "To provide high-quality products and build long-term strategic partnerships with our clients to ensure the continuous success and safety of their businesses.",
        valuesTitle: "Our Values",
        valuesDesc: "Integrity and transparency in dealings, commitment to delivery times, uncompromising quality, and putting customer satisfaction at the top of our priorities."
      },
      products: {
        title: "OUR PRODUCTS",
        headline: "Supply Divisions",
        desc: "Select a division to browse available products and request quotes for your company's needs.",
        viewProducts: "View Products"
      },
      category: {
        back: "Back to Divisions",
        commonProducts: "Common Products in this Division:",
        notFound: "Looking for a specific product not listed here?",
        requestQuote: "Request a Quote Now"
      },
      contact: {
        title: "CONTACT US",
        headline: "Ready to Meet Your Company's Needs",
        desc: "Send us your inquiries or quote requests, and our specialized team will respond as soon as possible.",
        email: "Email Address",
        hq: "Headquarters",
        location: "Riyadh, Saudi Arabia",
        formTitle: "Send a Message",
        nameLabel: "Company / Contact Name",
        namePlaceholder: "Enter name...",
        emailLabel: "Email Address",
        emailPlaceholder: "example@domain.com",
        msgLabel: "Message / Quote Request",
        msgPlaceholder: "Write the details of the products and quantities required here...",
        sendBtn: "Send Inquiry",
        sending: "Sending...",
        successMsg: "Your message has been sent successfully! We will contact you soon.",
        errorMsg: "An error occurred while sending. Please try again later."
      },
      partners: {
        title: "OUR PARTNERS",
        headline: "We Cherish Our Global Partnerships"
      },
      footer: {
        rights: "All rights reserved."
      }
    }
  };

  const t = translations[lang];

  // قوائم الكيماويات
  const chemicalItemsAr = [
    { title: "المذيبات عالية النقاء والسوائل", icon: "💧", products: ["Chloroform 99.8% AR", "N-Methyl 2-Pyrrolidone (NMP)", "N-Butanol 99.5% AR", "2,2,2-Trifluoroethanol 99%", "Distilled Water", "PC Klean Cleaning Solution"] },
    { title: "الأحماض والقواعد", icon: "🧪", products: ["Potassium Hydroxide 40% / 45%", "P-Toluic Acid 99%", "Picric Acid Solution"] },
    { title: "المحاليل القياسية (AAS Standards)", icon: "📏", products: ["Aluminium AAS Standard 1000mg/L", "Magnesium AAS Standard 1000mg/L", "Nickel AAS Standard 1000mg/L"] },
    { title: "الأغاروز والمواد البيولوجية", icon: "🧬", products: ["Agarose For Electrophoresis (High EEO)", "Agarose For Electrophoresis (Medium EEO)", "Agarose For Electrophoresis (Low EEO)", "ACES Buffer 99%", "CDTA Monohydrate 99% AR"] },
    { title: "البيروكسيدات", icon: "💥", products: ["Barium Peroxide 86%", "Benzoyl Peroxide 98%", "M-Chloroperbenzoic Acid (mCPBA) 65-77%"] },
    { title: "البوليمرات (PEG & PVP)", icon: "🔗", products: ["Polyethylene Glycol (PEG 200/300/400/600/1500)", "Polyvinyl Pyrrolidone (PVP K25/K30/K90)"] },
    { title: "الكواشف الدقيقة والمخبرية", icon: "🔬", products: ["Phenyl Fluorone 98.5% AR", "Phenyl Hydrazine 98%", "Quinhydrone Extra Pure", "Sodium Triacetoxy Borohydride 96%", "Tributyrin 97%", "Propiophenone 99%", "Pentaerythritol 98%", "Phloroglucinol 99% AR"] },
    { title: "الأملاح غير العضوية والمعدنية", icon: "🧂", products: ["Aluminium Chloride Hexahydrate 97%", "Ammonium Sulphate 0.5M Standardized", "Potassium Iodate 99.5% AR", "Potassium Oxalate 99.5% AR", "Silver Nitrate 0.5M Standardized"] }
  ];

  const chemicalItemsEn = [
    { title: "High Purity Solvents & Liquids", icon: "💧", products: ["Chloroform 99.8% AR", "N-Methyl 2-Pyrrolidone (NMP)", "N-Butanol 99.5% AR", "2,2,2-Trifluoroethanol 99%", "Distilled Water", "PC Klean Cleaning Solution"] },
    { title: "Acids & Bases", icon: "🧪", products: ["Potassium Hydroxide 40% / 45%", "P-Toluic Acid 99%", "Picric Acid Solution"] },
    { title: "AAS Standard Solutions", icon: "📏", products: ["Aluminium AAS Standard 1000mg/L", "Magnesium AAS Standard 1000mg/L", "Nickel AAS Standard 1000mg/L"] },
    { title: "Agarose & Biologicals", icon: "🧬", products: ["Agarose For Electrophoresis (High EEO)", "Agarose For Electrophoresis (Medium EEO)", "Agarose For Electrophoresis (Low EEO)", "ACES Buffer 99%", "CDTA Monohydrate 99% AR"] },
    { title: "Peroxides", icon: "💥", products: ["Barium Peroxide 86%", "Benzoyl Peroxide 98%", "M-Chloroperbenzoic Acid (mCPBA) 65-77%"] },
    { title: "Polymers (PEG & PVP)", icon: "🔗", products: ["Polyethylene Glycol (PEG 200/300/400/600/1500)", "Polyvinyl Pyrrolidone (PVP K25/K30/K90)"] },
    { title: "Fine Chemicals & Reagents", icon: "🔬", products: ["Phenyl Fluorone 98.5% AR", "Phenyl Hydrazine 98%", "Quinhydrone Extra Pure", "Sodium Triacetoxy Borohydride 96%", "Tributyrin 97%", "Propiophenone 99%", "Pentaerythritol 98%", "Phloroglucinol 99% AR"] },
    { title: "Inorganic & Metallic Salts", icon: "🧂", products: ["Aluminium Chloride Hexahydrate 97%", "Ammonium Sulphate 0.5M Standardized", "Potassium Iodate 99.5% AR", "Potassium Oxalate 99.5% AR", "Silver Nitrate 0.5M Standardized"] }
  ];

  // قوائم المستلزمات الطبية
  const medicalItemsAr = [
    { title: "الملابس والوقاية", icon: "🥼", products: ["قفازات طبية", "كمامات جراحية", "كمامات تنفسية", "ملابس طبية"] },
    { title: "التعقيم والتطهير", icon: "🧴", products: ["أدوات التعقيم", "مواد التعقيم والتطهير"] },
    { title: "رعاية المرضى", icon: "🛏️", products: ["مستلزمات العناية بالمرضى", "أدوات استهلاكية يومية"] }
  ];

  const medicalItemsEn = [
    { title: "Clothing & Protection", icon: "🥼", products: ["Medical Gloves", "Surgical Masks", "Respiratory Masks", "Medical Clothing"] },
    { title: "Sterilization", icon: "🧴", products: ["Sterilization Tools", "Disinfectants"] },
    { title: "Patient Care", icon: "🛏️", products: ["Patient Care Supplies", "Daily Consumables"] }
  ];

  // قوائم الضيافة
  const hospitalityItemsAr = [
    { title: "مستلزمات العناية الشخصية", icon: "🧴", products: ["شامبو", "شور جل", "كريم جسم", "بلسم", "صابون", "فرشاة أسنان مع معجون", "ماكينة حلاقة مع معجون", "مشط", "طقم تجميل", "غطاء رأس", "لوفة استحمام", "عدة خياطة", "ملمع أحذية", "لابيسة أحذية", "كيس صحي", "سليبر"] },
    { title: "المستلزمات الورقية", icon: "🧻", products: ["مناديل علب فندقي", "مناديل رول حمام", "ماكس رول", "اوتوكات", "انترفولد", "مناديل سفرة نابكن", "مناديل فلة", "مناديل مزدوجة"] },
    { title: "مستلزمات التنظيف", icon: "🧼", products: ["مطهر عام", "منظف حمامات", "معطر جو", "مطهر أرضيات", "جل منظف", "منعم ملابس", "منظف زجاج", "صابون أيدي", "مبيض ملابس", "سائل غسيل صحون", "كريم تنظيف للأسطح", "ملمع أثاث", "ملمع استيل", "ملمع أفران", "صابون غسيل بودرة", "شامبو سجاد"] },
    { title: "أدوات النظافة", icon: "🧹", products: ["مبيد حشرات", "أكياس نفايات", "اسفنج صحون", "سلك صحون", "مساحة قطن أرضيات", "موب أرضيات", "مكنسة", "عصا مكنسة", "مساحة بلاستيك", "جاروف", "فرشة حمام", "فوط مايكروفايبر", "مساحة بخاخ"] },
    { title: "اكسسوارات فندقية", icon: "🛎️", products: ["عربة نظافة", "عربة غرف", "عربة حقائب", "غلاية مياه", "طقم غلاية فندقي", "استشوار فندقي", "مكواة بخار", "طاولة مكواة", "حامل مكواة", "سطل دعاسة", "حاوية نفايات"] },
    { title: "مستلزمات الكافيه والمطبخ", icon: "☕", products: ["أكواب شاي وقهوة", "أكواب عصير", "كاسات مياه بلاستيك", "شاي", "قهوة سريعة التحضير", "مبيض قهوة", "سكر أصابع ودايت", "محرك خشب", "قصدير وورق زبدة", "رول تغليف", "شمعة تسخين", "أطباق ميكرويف", "كاب شيف ومريولة", "قفازات", "مجموعة طعام"] },
    { title: "المفروشات", icon: "🛏️", products: ["شرشف سرير", "كيس لحاف", "حشوة لحاف", "كيس مخدة", "مخدة", "واقي مرتبة", "كشكش سرير", "حزام ديكور", "منشفة حمام", "منشفة وجه ويد", "دعاسة حمام", "روب حمام", "منشفة استخدام مرة واحدة"] }
  ];

  const hospitalityItemsEn = [
    { title: "Personal Care", icon: "🧴", products: ["Shampoo", "Shower Gel", "Body Lotion", "Conditioner", "Soap", "Toothbrush with Paste", "Shaving Kit with Paste", "Comb", "Vanity Kit", "Shower Cap", "Loofah", "Sewing Kit", "Shoe Shine", "Shoehorn", "Sanitary Bag", "Slippers"] },
    { title: "Paper Supplies", icon: "🧻", products: ["Facial Tissues", "Toilet Paper Rolls", "Maxi Rolls", "Autocut Rolls", "Interfold Tissues", "Napkins", "Bulk Tissues", "Double-ply Tissues"] },
    { title: "Cleaning Supplies", icon: "🧼", products: ["General Disinfectant", "Toilet Cleaner", "Air Freshener", "Floor Disinfectant", "Cleaning Gel", "Fabric Softener", "Glass Cleaner", "Hand Soap", "Bleach", "Dishwashing Liquid", "Surface Cream Cleaner", "Furniture Polish", "Steel Polish", "Oven Polish", "Washing Powder", "Carpet Shampoo"] },
    { title: "Cleaning Tools", icon: "🧹", products: ["Insecticide", "Garbage Bags", "Dish Sponges", "Steel Wool", "Cotton Mops", "Floor Mops", "Brooms", "Broomsticks", "Plastic Squeegees", "Dustpans", "Toilet Brushes", "Microfiber Towels", "Spray Mops"] },
    { title: "Hotel Accessories", icon: "🛎️", products: ["Cleaning Carts", "Housekeeping Carts", "Luggage Carts", "Water Kettles", "Hotel Kettle Sets", "Hair Dryers", "Steam Irons", "Ironing Boards", "Iron Holders", "Pedal Bins", "Waste Bins"] },
    { title: "Cafe & Kitchen Supplies", icon: "☕", products: ["Tea & Coffee Cups", "Juice Cups", "Plastic Water Cups", "Tea", "Instant Coffee", "Coffee Creamer", "Sugar Sticks & Diet", "Wooden Stirrers", "Aluminum Foil & Baking Paper", "Cling Film", "Chafing Fuel", "Microwave Containers", "Chef Caps & Aprons", "Gloves", "Cutlery Sets"] },
    { title: "Bedding & Furnishings", icon: "🛏️", products: ["Bed Sheets", "Duvet Covers", "Duvet Inserts", "Pillowcases", "Pillows", "Mattress Protectors", "Bed Skirts", "Bed Runners", "Bath Towels", "Face & Hand Towels", "Bath Mats", "Bathrobes", "Disposable Towels"] }
  ];

  // مسارات الصور تعتمد على ملفاتك بصيغة PNG
  const productSections = [
    {
      id: "chemicals",
      title: lang === "ar" ? "الكيماويات" : "Chemicals",
      description: lang === "ar" 
        ? "مجموعة متكاملة من الكيماويات الأساسية والمتخصصة لتلبية احتياجات مختلف القطاعات والصناعات."
        : "A comprehensive range of essential and specialized chemicals to meet the needs of various sectors and industries.",
      icon: "🧪",
      image: "/chemicals.png", 
      items: lang === "ar" ? chemicalItemsAr : chemicalItemsEn
    },
    {
      id: "medical",
      title: lang === "ar" ? "المستلزمات الطبية" : "Medical Supplies",
      description: lang === "ar"
        ? "توفير أحدث المستلزمات الطبية والأدوات الاستهلاكية عالية الجودة للمستشفيات والمراكز الصحية."
        : "Providing the latest high-quality medical supplies and consumables for hospitals and health centers.",
      icon: "🩺",
      image: "/medical.png", 
      items: lang === "ar" ? medicalItemsAr : medicalItemsEn
    },
    {
      id: "hospitality",
      title: lang === "ar" ? "الضيافة" : "Hospitality",
      description: lang === "ar"
        ? "حلول متكاملة ومستلزمات عالية الجودة لقطاع الفنادق والضيافة لضمان تقديم تجربة استثنائية للضيوف."
        : "Integrated solutions and high-quality supplies for the hotel and hospitality sector to ensure an exceptional guest experience.",
      icon: "🏨",
      image: "/hospitality.png", 
      items: lang === "ar" ? hospitalityItemsAr : hospitalityItemsEn
    }
  ];

  const partners = [
    {
      name: "Xilong Scientific",
      website: "https://www.xilongs.com/about.html",
      logoUrl: "/xilong-logo.png", 
      logoText: "XILONG",
      logoSubText: "SCIENTIFIC"
    },
    {
      name: "CHEMCO GLOBAL",
      website: "#", 
      logoUrl: "/Chemco Main Logo 2400x1800.jpg", 
      logoText: null,
      logoSubText: null
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    const formData = new FormData(e.target);
    
    formData.append("access_key", "1b42bf49-250a-44c8-b730-9264ed9c01a8");
    formData.append("subject", `استفسار جديد من الموقع: ${formData.get("name")}`);
    formData.append("from_name", "موقع Global Golden Elements");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSubmitMessage({ type: "success", text: t.contact.successMsg });
        e.target.reset();
      } else {
        setSubmitMessage({ type: "error", text: t.contact.errorMsg });
      }
    } catch (error) {
      setSubmitMessage({ type: "error", text: t.contact.errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleLang = () => {
    setLang(lang === "ar" ? "en" : "ar");
  };

  // 1. القائمة العلوية
  const Header = () => (
    <header 
      className={`fixed top-0 left-0 w-full z-50 bg-white/95 backdrop-blur-md border-b-4 border-[#C59B3C] p-4 md:px-8 flex justify-between items-center shadow-lg transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`}
    >
      <div className="cursor-pointer transition-transform hover:scale-105 py-1" onClick={() => setPage("home")}>
        <img 
          src="/Global-Golden-Elements-Main-Logo-2400x1800-1.png" 
          alt="GLOBAL GOLDEN ELEMENTS" 
          className="h-24 md:h-32 lg:h-36 max-w-[250px] md:max-w-[320px] object-contain"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/Global-Golden-Elements-Main-Logo-2400x1800-1.jpg";
          }}
        />
      </div>
      <nav className="hidden md:flex gap-8 font-bold text-[#0B3A75] text-lg items-center">
        {['home', 'about', 'products', 'partners', 'contact'].map((navItem) => {
          const isActive = page === navItem || (navItem === 'products' && page === 'categoryDetail');
          return (
            <button 
              key={navItem}
              onClick={() => setPage(navItem)} 
              className={`pb-2 transition-all ${isActive ? 'text-[#C59B3C] border-b-2 border-[#C59B3C]' : 'hover:text-[#C59B3C]'}`}
            >
              {t.nav[navItem]}
            </button>
          );
        })}
        <button 
          onClick={toggleLang} 
          className="flex items-center gap-2 ml-4 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0B3A75] text-sm transition-colors"
        >
          <Globe className="w-4 h-4" />
          {lang === "ar" ? "English" : "العربية"}
        </button>
      </nav>
      
      {/* Mobile Menu */}
      <div className="md:hidden flex items-center gap-3 text-[#0B3A75] font-bold">
        <button onClick={toggleLang} className="p-2 bg-slate-100 rounded-full">
          <Globe className="w-5 h-5" />
        </button>
        <select 
          className="bg-transparent border-none outline-none text-lg text-right"
          value={page === 'categoryDetail' ? 'products' : page}
          onChange={(e) => setPage(e.target.value)}
        >
          <option value="home">{t.nav.home}</option>
          <option value="about">{t.nav.about}</option>
          <option value="products">{t.nav.products}</option>
          <option value="partners">{t.nav.partners}</option>
          <option value="contact">{t.nav.contact}</option>
        </select>
      </div>
    </header>
  );

  // 2. الصفحة الرئيسية
  const Home = () => (
    <section 
      className="flex flex-col items-center justify-end min-h-[85vh] pb-24 text-center px-6 animate-fade-in relative overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url('/LogoAi - Global Golden Elements Watermark8.jpg')` }}
    >
       <div className="absolute inset-0 bg-gradient-to-t from-[#0B3A75] via-[#0B3A75]/40 to-black/20"></div>
       
       <div className="relative z-10 flex flex-col items-center mt-auto max-w-4xl mx-auto pt-32">
         <p className="text-[#C59B3C] font-black mb-4 text-xl md:text-2xl tracking-widest uppercase drop-shadow-lg">
           {t.hero.welcome}
         </p>
         <p className="text-white text-lg md:text-2xl leading-relaxed mb-10 drop-shadow-md">
           {t.hero.desc}
         </p>
         <div className="flex flex-wrap justify-center gap-4">
           <button 
              onClick={() => setPage("products")}
              className="px-8 py-4 rounded-full bg-[#C59B3C] text-white font-black text-lg hover:bg-[#b08830] shadow-xl hover:-translate-y-1 transition-all"
            >
              {t.hero.btnProducts}
            </button>
            <button 
              onClick={() => setPage("about")}
              className="px-8 py-4 rounded-full bg-white/20 backdrop-blur-md border-2 border-white text-white font-black text-lg hover:bg-white hover:text-[#0B3A75] transition-all"
            >
              {t.hero.btnAbout}
            </button>
         </div>
       </div>
    </section>
  );

  // 3. صفحة من نحن
  const About = () => (
    <section className="max-w-7xl mx-auto px-6 py-20 pt-40 animate-fade-in">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <p className="text-[#C59B3C] font-black mb-3 text-lg">{t.about.title}</p>
        <h1 className="text-4xl md:text-5xl font-black text-[#0B3A75] mb-6">{t.about.headline}</h1>
        <p className="text-slate-600 leading-8 text-lg" dangerouslySetInnerHTML={{ __html: t.about.desc }} />
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white p-8 rounded-3xl border-t-4 border-[#0B3A75] shadow-lg hover:shadow-xl transition-all text-center group">
          <div className="w-20 h-20 mx-auto bg-[#0B3A75] text-[#C59B3C] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <Target className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-black text-[#0B3A75] mb-4">{t.about.visionTitle}</h3>
          <p className="text-slate-600 leading-relaxed">{t.about.visionDesc}</p>
        </div>

        <div className="bg-white p-8 rounded-3xl border-t-4 border-[#C59B3C] shadow-lg hover:shadow-xl transition-all text-center group">
          <div className="w-20 h-20 mx-auto bg-[#C59B3C] text-white rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <Shield className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-black text-[#0B3A75] mb-4">{t.about.missionTitle}</h3>
          <p className="text-slate-600 leading-relaxed">{t.about.missionDesc}</p>
        </div>

        <div className="bg-white p-8 rounded-3xl border-t-4 border-[#0B3A75] shadow-lg hover:shadow-xl transition-all text-center group">
          <div className="w-20 h-20 mx-auto bg-[#0B3A75] text-[#C59B3C] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <Award className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-black text-[#0B3A75] mb-4">{t.about.valuesTitle}</h3>
          <p className="text-slate-600 leading-relaxed">{t.about.valuesDesc}</p>
        </div>
      </div>
    </section>
  );

  // 4. صفحة المنتجات الرئيسية
  const Products = () => (
    <section className="max-w-7xl mx-auto px-6 py-20 pt-40 animate-fade-in">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <p className="text-[#C59B3C] font-black mb-3 text-lg">{t.products.title}</p>
        <h1 className="text-4xl md:text-5xl font-black text-[#0B3A75] mb-5">{t.products.headline}</h1>
        <p className="text-slate-600 leading-8 text-lg">{t.products.desc}</p>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-8">
        {productSections.map((section) => (
          <div 
            key={section.id} 
            onClick={() => {
              setActiveCategory(section);
              setPage("categoryDetail");
            }}
            className="group cursor-pointer rounded-3xl border border-slate-100 shadow-md hover:shadow-2xl hover:border-[#C59B3C] hover:-translate-y-2 transition-all p-8 bg-white flex flex-col items-center text-center"
          >
            <div className="w-24 h-24 rounded-full bg-slate-50 border-2 border-[#0B3A75] text-[#0B3A75] group-hover:bg-[#0B3A75] group-hover:text-white flex items-center justify-center mb-6 text-4xl transition-colors duration-300">
              {section.icon}
            </div>
            <h3 className="text-2xl font-black text-[#0B3A75] mb-4">{section.title}</h3>
            <p className="text-slate-600 leading-relaxed mb-6">{section.description}</p>
            <span className="mt-auto text-[#C59B3C] font-bold flex items-center gap-2 group-hover:gap-4 transition-all">
              {t.products.viewProducts} <ArrowRight className={`w-5 h-5 ${lang === 'ar' ? 'rotate-180' : ''}`} />
            </span>
          </div>
        ))}
      </div>
    </section>
  );

  // 5. صفحة تفاصيل القسم
  const CategoryDetail = () => (
    <section className="min-h-screen pt-40 px-4 pb-20 animate-fade-in">
      <div className="max-w-5xl mx-auto mb-8">
        <button 
          onClick={() => setPage("products")}
          className="flex items-center gap-2 text-slate-500 hover:text-[#C59B3C] font-bold transition-colors"
        >
          <ArrowRight className={`w-6 h-6 ${lang === 'en' ? 'rotate-180' : ''}`} />
          {t.category.back}
        </button>
      </div>

      {activeCategory && (
        <div className="max-w-5xl mx-auto bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
          <div className="h-64 md:h-96 w-full relative">
            <img 
              src={activeCategory.image} 
              alt={activeCategory.title} 
              className="w-full h-full object-cover" 
              onError={(e) => {
                // الكود الذكي لتشغيل الصور حتى لو قام الويندوز بتغيير أسمائها
                const fallbacks = [
                  activeCategory.image.replace('.png', '.jpg.png'),
                  activeCategory.image.replace('.png', '.jpg'),
                  activeCategory.image.replace('.png', '.png.png')
                ];
                let attempt = parseInt(e.target.dataset.attempt || "0");
                if (attempt < fallbacks.length) {
                  e.target.src = fallbacks[attempt];
                  e.target.dataset.attempt = attempt + 1;
                }
              }}
            />
            <div className="absolute inset-0 bg-[#0B3A75]/60 flex items-center justify-center">
              <h1 className="text-4xl md:text-5xl font-black text-white">{activeCategory.title}</h1>
            </div>
          </div>
          
          <div className="p-8 md:p-12">
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">{activeCategory.description}</p>
            
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100">
              <h3 className="text-2xl font-black text-[#0B3A75] mb-6 flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#C59B3C] flex-shrink-0" />
                {t.category.commonProducts}
              </h3>
              
              {activeCategory.items.length > 0 && typeof activeCategory.items[0] === 'object' ? (
                <div className="space-y-6">
                  {activeCategory.items.map((subCat, index) => (
                     <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:border-[#C59B3C] transition-colors">
                      <h4 className="text-xl font-bold text-[#0B3A75] mb-4 flex items-center gap-2">
                        <span className="text-2xl">{subCat.icon}</span> {subCat.title}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {subCat.products.map((prod, pIdx) => (
                          <span key={pIdx} className="px-3 py-1.5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg border border-slate-200">
                            {prod}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {activeCategory.items.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white rounded-xl shadow-sm border border-slate-100 p-4 hover:border-[#C59B3C] transition-colors">
                      <div className="w-2 h-2 rounded-full bg-[#C59B3C] mt-2 flex-shrink-0"></div>
                      <span className="font-bold text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 text-center pt-8 border-t border-slate-200">
                 <p className="text-slate-500 mb-4">{t.category.notFound}</p>
                 <button 
                   onClick={() => setPage("contact")}
                   className="px-6 py-3 rounded-xl bg-[#0B3A75] text-white font-bold hover:bg-[#C59B3C] transition-colors"
                 >
                   {t.category.requestQuote}
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  // 6. صفحة التواصل
  const Contact = () => (
    <section className="max-w-7xl mx-auto px-6 py-20 pt-40 animate-fade-in">
      <div className="rounded-3xl bg-[#0B3A75] text-white p-8 md:p-12 grid lg:grid-cols-2 gap-12 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#C59B3C] rounded-full blur-[80px] opacity-20 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C59B3C] rounded-full blur-[80px] opacity-20 transform -translate-x-1/2 translate-y-1/2"></div>

        <div className="flex flex-col justify-center relative z-10">
          <p className="text-[#C59B3C] font-black mb-3 tracking-wider text-lg uppercase">{t.contact.title}</p>
          <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">{t.contact.headline}</h1>
          <p className="text-blue-100 leading-relaxed mb-10 text-lg">{t.contact.desc}</p>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
              <div className="bg-[#C59B3C] p-3 rounded-xl flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-200">{t.contact.email}</p>
                <span className="text-lg font-bold">info@global-ge.com</span>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
              <div className="bg-[#C59B3C] p-3 rounded-xl flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-200">{t.contact.hq}</p>
                <span className="text-lg font-bold">{t.contact.location}</span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleContactSubmit} className="bg-white p-8 rounded-2xl shadow-xl relative z-10 text-slate-800">
          <h3 className="text-2xl font-black text-[#0B3A75] mb-6">{t.contact.formTitle}</h3>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-slate-600 mb-2">{t.contact.nameLabel}</label>
              <input type="text" name="name" required className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#C59B3C] focus:border-transparent transition-all" placeholder={t.contact.namePlaceholder} />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-600 mb-2">{t.contact.emailLabel}</label>
              <input type="email" name="email" required className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#C59B3C] focus:border-transparent transition-all" placeholder={t.contact.emailPlaceholder} />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-600 mb-2">{t.contact.msgLabel}</label>
              <textarea name="message" required rows="4" className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#C59B3C] focus:border-transparent resize-none transition-all" placeholder={t.contact.msgPlaceholder}></textarea>
            </div>
            
            {submitMessage && (
              <div className={`p-4 rounded-xl font-bold text-sm text-center ${submitMessage.type === 'success' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                {submitMessage.text}
              </div>
            )}

            <button disabled={isSubmitting} type="submit" className={`w-full py-4 rounded-xl text-white font-black text-lg transition-colors shadow-md ${isSubmitting ? 'bg-slate-400 cursor-not-allowed' : 'bg-[#C59B3C] hover:bg-[#b08830]'}`}>
              {isSubmitting ? t.contact.sending : t.contact.sendBtn}
            </button>
          </div>
        </form>
      </div>
    </section>
  );

  // 7. صفحة الشركاء
  const PartnersSection = () => (
    <section className="max-w-7xl mx-auto px-6 py-20 pt-40 animate-fade-in">
      <div className="text-center mb-12">
        <p className="text-[#C59B3C] font-black mb-2 uppercase">{t.partners.title}</p>
        <h2 className="text-3xl md:text-4xl font-black text-[#0B3A75]">{t.partners.headline}</h2>
        <p className="text-slate-600 leading-8 text-lg mt-4 max-w-2xl mx-auto">
          {lang === 'ar' ? 'نفخر بالتعاون مع نخبة من أفضل الشركات والمصنعين العالميين لضمان تقديم أعلى معايير الجودة لعملائنا.' : 'We are proud to collaborate with elite global companies and manufacturers to ensure delivering the highest quality standards to our clients.'}
        </p>
      </div>
      
      <div className="grid sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {partners.map((partner) => (
          <a key={partner.name} href={partner.website} target="_blank" rel="noreferrer" className="group block">
            <div className="rounded-3xl border-2 border-slate-100 bg-white shadow-md hover:shadow-2xl hover:border-[#C59B3C] transition-all duration-300 p-8 flex flex-col items-center justify-center min-h-[240px] bg-slate-800">
              {partner.logoUrl ? (
                <img 
                  src={partner.logoUrl} 
                  alt={partner.name} 
                  className="max-h-32 w-auto object-contain mb-6 filter group-hover:scale-110 transition-transform duration-300" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://placehold.co/300x150/ffffff/0B3A75?text=LOGO";
                  }}
                />
              ) : (
                <div className="text-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <p className="text-4xl md:text-5xl font-black tracking-widest text-red-600 leading-none drop-shadow-sm">{partner.logoText}</p>
                  <p className="text-sm md:text-base font-bold tracking-[0.3em] text-white mt-2">{partner.logoSubText}</p>
                </div>
              )}
              <h3 className="text-xl font-black text-slate-400 group-hover:text-white transition-colors mt-auto pt-4 border-t border-slate-700 w-full text-center">
                {partner.name}
              </h3>
            </div>
          </a>
        ))}
      </div>
    </section>
  );

  return (
    <main dir={lang === "ar" ? "rtl" : "ltr"} className="min-h-screen bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-fixed text-slate-900 font-sans flex flex-col">
      <style>{`
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; opacity: 0; }
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(15px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
      `}</style>
      
      <Header />
      
      <div className="flex-grow">
        {page === "home" && <Home />}
        {page === "about" && <About />}
        {page === "products" && <Products />}
        {page === "categoryDetail" && <CategoryDetail />}
        {page === "partners" && <PartnersSection />}
        {page === "contact" && <Contact />}
      </div>
      
      <footer className="bg-[#0B3A75] text-white py-12 text-center mt-auto">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center">
           <div className="bg-white p-4 rounded-2xl mb-8 shadow-lg inline-block">
               <img 
                  src="/Global-Golden-Elements-Main-Logo-2400x1800-1.png" 
                  alt="GLOBAL GOLDEN ELEMENTS" 
                  className="h-20 md:h-24 object-contain"
                  onError={(e) => { 
                    if (!e.target.dataset.retried) {
                      e.target.dataset.retried = true;
                      e.target.src = '/Global-Golden-Elements-Main-Logo-2400x1800-1.jpg'; 
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
               />
           </div>
           
           <p className="font-semibold text-blue-200 mb-2">© 2026 GLOBAL GOLDEN ELEMENTS.</p>
           <p className="text-sm text-blue-400">{t.footer.rights}</p>
        </div>
      </footer>
    </main>
  );
}